// =====================================================================
// src/modulos/manutencao.js — a lista de chamados, por perfil
//
// O que a ficha da camareira abre (0029), esta tela fecha. Sem ela, o
// "Solicito" da camareira some dentro do banco e ninguém conserta nada.
//
// DE ONDE VEIO O DESENHO: `_renderIntercorrenciaItem` do app do Monteiro
// (index.html, 23291) — o cartão de fundo rosa com borda #fca5a5, o
// cabeçalho colorido com o número do quarto, a data em 11px cinza à
// direita, a foto acima da descrição, e os botões no rodapé. O verde do
// resolvido (#f0fdf4 / #86efac) é o mesmo de lá.
//
// ONDE DIVERGE, E POR QUÊ:
//
//   · Lá é uma modal; aqui é um módulo. Lá a intercorrência é um aviso
//     solto que o gestor lê; aqui é a fila de trabalho da manutenção, do
//     proprietário e da governança — três perfis, o dia inteiro.
//
//   · Duas pilhas: PARA VOCÊ e CIÊNCIA. Lá não existe, porque lá só o
//     gestor abre a tela. Aqui a mesma lista é lida por quem conserta e por
//     quem só acompanha, e a separação vem de `para_mim`, que o banco
//     calcula (0029). Sem ela, o proprietário veria um botão "Resolver" em
//     chamado do hotel e levaria um 42501 na cara.
//
//   · A pílula HOTEL / DONO, que lá não existe: veio da divisão que o Hugo
//     deu em 17/09 (docs/quem-faz-o-que-no-garvey.md).
//
//   · Lá o "Sim/Não" resolve na hora. Aqui `resolver_chamado` aceita uma
//     observação, e ela é o que vira histórico — então o fechamento pede
//     confirmação com um campo, em vez de um clique seco.
//
//   · A foto: lá o bucket é público e o `<img src>` aponta direto para ele.
//     Aqui o bucket é PRIVADO (0029) e a tela pede uma URL assinada de vida
//     curta, sob demanda. Não se carregam N URLs assinadas ao abrir a lista:
//     seriam N requisições para fotos que talvez ninguém abra.
//
// 0059 (Hugo, 24/09 — revisão da proposta): o chamado passa pela
// SUPERVISORA antes da manutenção, e a tela passa a mostrar a fila inteira
// ("Em aberto") com a etapa de cada chamado e os botões da etapa: validar
// ou descartar (supervisora), resolver ou "não consigo" (manutenção),
// avisar o proprietário ou devolver (supervisora). Os degraus da 0033 não
// tinham tela até aqui — o banco os fazia, e ninguém os alcançava.
//
// QUEM VÊ O QUÊ NÃO ESTÁ AQUI. Está no RLS e em `chamados_do_cliente`.
// Tela some com um inspetor aberto.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, barraDeAbas, enquantoAtualiza } from '../ui.js';
import {
  responsavelDe, origemDe, separarPorAcao, ordenar,
  atrasoDe, tituloDoChamado, contarChamados, vazioDe, dataCurta,
  etapaDe, acoesDoChamado, ACOES_DO_CHAMADO,
} from '../lib/manutencao.js';
import { dataPorExtenso } from '../lib/governanca.js';

const INTERVALO_MS = 60000;   // a fila de manutenção não muda de segundo em segundo

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  // Sem `podeEditar` aqui: quem pode fechar cada chamado vem do banco, em
  // `para_mim`. O nível do módulo não basta — o proprietário só VÊ
  // manutenção e mesmo assim fecha os chamados das unidades dele.
  let vivo = true, timer = null;
  let aba = 'em_aberto';
  let linhas = [];
  let carregando = true;

  async function carregar() {
    linhas = await auth.rpc('chamados_do_cliente', { p_situacao: aba });   // 'em_aberto' = as quatro etapas (0059)
    carregando = false;
  }
  function refazer() { return enquantoAtualiza(async () => { await carregar(); desenhar(); }); }

  // ── o cartão, copiado do Monteiro ──────────────────────────────────
  function cartao(c) {
    const resolvido = c.situacao === 'resolvido' || c.situacao === 'cancelado';
    const cor = resolvido ? '#059669' : '#dc2626';
    const etapa = etapaDe(c.situacao);
    const resp = responsavelDe(c.responsavel);
    const org = origemDe(c.origem);
    const atraso = resolvido ? null : atrasoDe(c.aberto_em, agora());

    const caixa = el('div', { class: 'chamado' + (resolvido ? ' resolvido' : ''),
      dataset: { chamado: c.id, quarto: c.unidade, responsavel: c.responsavel, meu: c.para_mim ? '1' : '0', situacao: c.situacao } });

    caixa.appendChild(el('div', { class: 'chamado-cabeca' },
      el('div', { class: 'quem', style: { color: cor } },
        icone(resolvido ? 'ic-check' : org.icone),
        `Quarto ${c.unidade}`,
        c.andar != null ? el('span', { class: 'andar' }, ` · ${c.andar}º andar`) : null),
      el('div', { class: 'quando' }, dataCurta(c.aberto_em, agora()))));

    caixa.appendChild(el('div', { class: 'chamado-titulo' },
      el('strong', null, tituloDoChamado(c)),
      el('span', { class: 'ficha-dono ' + resp.classe, title: `Conserto é do ${resp.rotulo.toLowerCase()}` }, resp.curto),
      atraso ? el('span', { class: 'chamado-atraso ' + atraso.nivel }, atraso.texto) : null));
    caixa.appendChild(el('div', { class: 'chamado-etapa ' + etapa.classe, dataset: { etapa: c.situacao } }, etapa.rotulo,
      c.situacao === 'sem_solucao' && c.sem_solucao_motivo ? el('span', { class: 'obs' }, ` — ${c.sem_solucao_motivo}`) : null,
      c.situacao === 'escalado' && c.escalado_motivo ? el('span', { class: 'obs' }, ` — ${c.escalado_motivo}`) : null,
      c.situacao === 'aberto' && c.sem_solucao_motivo ? el('span', { class: 'obs' }, ` — devolvido: ${c.sem_solucao_motivo}`) : null));

    // A foto só chega quando alguém quer vê-la: bucket privado, URL assinada
    // de vida curta. Botão, e não `<img>` — carregar N assinaturas para uma
    // lista que ninguém vai abrir inteira é desperdício e vazamento de link.
    if (c.foto_caminho) {
      const lugar = el('div', { class: 'chamado-foto' });
      const ver = el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { foto: c.id },
        onclick: async () => {
          ver.disabled = true;
          try {
            const url = await auth.urlAssinada('intercorrencias', c.foto_caminho, 300);
            lugar.replaceChildren(el('img', { class: 'ficha-foto-previa', src: url, alt: `Foto do chamado do quarto ${c.unidade}` }));
          } catch (e) { aviso(e.message, 'erro'); ver.disabled = false; }
        } }, icone('ic-camera'), 'Ver foto');
      lugar.appendChild(ver);
      caixa.appendChild(lugar);
    }

    caixa.appendChild(el('div', { class: 'chamado-descricao' }, c.descricao || ''));
    caixa.appendChild(el('div', { class: 'chamado-rodape' },
      el('span', { class: 'por' }, c.aberto_por_nome ? `Aberto por ${c.aberto_por_nome}` : 'Aberto pelo sistema'),
      resolvido
        ? el('span', { class: 'fechado' },
            c.fechado_por_nome ? `${c.situacao === 'cancelado' ? 'Descartado' : 'Resolvido'} por ${c.fechado_por_nome}` : etapa.rotulo,
            c.fechado_em ? ` em ${dataCurta(c.fechado_em, agora())}` : '',
            c.fechado_obs ? el('span', { class: 'obs' }, ` — ${c.fechado_obs}`) : null)
        // `para_mim` JA E "eu posso fechar este" — o banco calcula isso
        // espelhando `resolver_chamado` (0029). Eu tinha escrito
        // `para_mim && podeEditar` aqui, e a prova pegou: o papel
        // `proprietario` tem `ver manutencao` e nada mais, então o dono
        // ficava sem o botão de fechar o chamado DELE. A regra mora num
        // lugar só; repeti-la aqui foi inventar uma segunda, mais rígida.
        : (c.para_mim
            ? el('span', { class: 'chamado-acoes', style: { display: 'inline-flex', gap: '6px', flexWrap: 'wrap' } },
                acoesDoChamado(c).map(a => el('button', { type: 'button',
                  class: 'btn btn-sm ' + (a === 'resolver' || a === 'validar' ? 'btn-verde' : a === 'escalar' ? 'btn-primary' : 'btn-secondary'),
                  dataset: { acao: a, [a === 'resolver' ? 'resolver' : 'acaoChamado']: c.id },
                  onclick: () => modalAcao(c, a) },
                  a === 'resolver' || a === 'validar' ? icone('ic-check') : null, ACOES_DO_CHAMADO[a].rotulo)))
            : el('span', { class: 'ciencia' }, 'Ciência'))));
    return caixa;
  }

  // ── agir num chamado (0033 + 0059) ─────────────────────────────────
  // Uma modal para as seis ações: o título diz o chamado, o campo aparece
  // quando a ação tem texto, e diz se é obrigatório — como o banco, que
  // recusa sem motivo (e a mensagem dele vai para o aviso).
  function modalAcao(c, acao) {
    const a = ACOES_DO_CHAMADO[acao];
    const raiz = el('div', { id: acao === 'resolver' ? 'resolver-chamado' : 'acao-chamado', dataset: { chamado: c.id, acao } });
    const campo = a.campo ? el('textarea', { class: 'input', id: acao === 'resolver' ? 'resolver-obs' : 'acao-texto', rows: '3', placeholder: a.dica }) : null;
    const botao = el('button', { type: 'button', class: 'btn ' + (acao === 'descartar' || acao === 'sem_solucao' ? 'btn-secondary' : 'btn-verde'),
      id: acao === 'resolver' ? 'btn-confirmar-resolver' : 'btn-confirmar-acao' }, a.rotulo);
    botao.onclick = async () => {
      const texto = campo ? campo.value.trim() : '';
      if (a.obrigatorio && !texto) { aviso('Escreva o motivo.', 'erro'); campo.focus(); return; }
      botao.disabled = true;
      const args = { p_chamado: c.id };
      if (a.campo) args[a.campo] = texto || null;
      try { await auth.rpc(a.rpc, args); }
      catch (e) { aviso(e.message, 'erro'); botao.disabled = false; return; }
      fecharModal();
      await refazer();
      aviso(`Quarto ${c.unidade}: ${a.rotulo.toLowerCase()} — feito.`);
    };
    raiz.appendChild(el('h2', { class: 'modal-title' }, `Quarto ${c.unidade} — ${tituloDoChamado(c)}`));
    raiz.appendChild(el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '12px' } }, c.descricao || ''));
    if (campo) raiz.appendChild(el('div', null,
      el('label', { class: 'label', for: campo.id }, a.obrigatorio ? 'Motivo' : (acao === 'resolver' ? 'O que foi feito (opcional)' : 'Observação (opcional)')), campo));
    raiz.appendChild(el('div', { class: 'ficha-rodape' },
      el('button', { type: 'button', class: 'btn btn-secondary', onclick: fecharModal }, 'Voltar'), botao));
    abrirModal(raiz, { largura: '460px' });
    if (campo) campo.focus();
  }

  // ── desenho ────────────────────────────────────────────────────────
  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'manutencao';

    const c = contarChamados(linhas);
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', null,
        el('div', { style: { fontSize: '13px', color: '#6b7280' } },
          'O que a camareira pediu na ficha e o que foi reportado como intercorrência.'),
        el('div', { style: { fontSize: '12px', color: '#9ca3af' } },
          `${dataPorExtenso(new Date(agora()))} · atualiza a cada minuto`)),
      el('div', { class: 'gov-resumo', id: 'resumo-chamados' },
        c.meus ? el('span', { class: 'pill pill-saida', dataset: { conta: 'meus' } }, icone('ic-wrench'), `${c.meus} PARA VOCÊ`) : null,
        c.ciencia ? el('span', { class: 'pill pill-entrada', dataset: { conta: 'ciencia' } }, icone('ic-eye'), `${c.ciencia} CIÊNCIA`) : null)));

    cont.appendChild(barraDeAbas('abas-chamados', [
      { chave: 'em_aberto', rotulo: 'Em aberto' },
      { chave: 'resolvido', rotulo: 'Resolvidos' },
      { chave: 'cancelado', rotulo: 'Descartados' },
    ], aba, async chave => {
      if (chave === aba) return;
      aba = chave; carregando = true; desenhar();
      await refazer();
    }));

    if (carregando) {
      cont.appendChild(el('div', { class: 'vazio', id: 'chamados-carregando', style: { padding: '30px', textAlign: 'center' } }, 'Carregando…'));
      return;
    }

    const { meus, ciencia } = separarPorAcao(linhas);
    const pilha = (titulo, lista, id) => {
      if (!lista.length) return;
      cont.appendChild(el('div', { class: 'andar-titulo', dataset: { pilha: id } }, titulo,
        el('span', { class: 'quem' }, ` · ${lista.length}`)));
      cont.appendChild(el('div', { class: 'chamados', id }, ordenar(lista, aba).map(cartao)));
    };

    if (!linhas.length) {
      cont.appendChild(el('div', { class: 'vazio', id: 'chamados-vazio', style: { padding: '40px 20px', textAlign: 'center' } },
        el('div', { style: { fontSize: '16px', fontWeight: '600', marginBottom: '6px' } }, vazioDe(aba, false))));
      return;
    }
    if (aba === 'resolvido' || aba === 'cancelado') {
      // Resolvido é histórico: não se separa em "para você", porque já foi.
      cont.appendChild(el('div', { class: 'chamados', id: 'chamados-resolvidos' }, ordenar(linhas, aba).map(cartao)));
      return;
    }
    pilha('PARA VOCÊ RESOLVER', meus, 'chamados-meus');
    pilha('CIÊNCIA — de outro responsável', ciencia, 'chamados-ciencia');
    if (!meus.length) {
      cont.appendChild(el('div', { class: 'vazio', id: 'chamados-sem-meus', style: { padding: '14px 20px', textAlign: 'center', fontSize: '13px' } },
        vazioDe(aba, ciencia.length > 0)));
    }
  }

  // ── ciclo de vida ──────────────────────────────────────────────────
  (async () => {
    try { await carregar(); } catch (e) { aviso(e.message, 'erro'); carregando = false; }
    desenhar();
    timer = setInterval(async () => {
      if (!vivo || document.getElementById('modal')) return;   // não puxa o tapete de quem está resolvendo
      try { await carregar(); desenhar(); } catch (_) { /* um ciclo perdido não é erro para mostrar */ }
    }, INTERVALO_MS);
  })();

  return () => { vivo = false; if (timer) clearInterval(timer); };
}
